import { formatMoney } from "./format";

/** Presentation contracts, not protocol DTOs or transaction validators. */
export type AuthorityTier = "T1" | "T2" | "T3";
export type AuthorityState = "authorized" | "approval-required" | "revoked";

export interface AuthorityView {
  tier: AuthorityTier;
  state: AuthorityState;
  label: string;
  detail?: string;
}

export interface MoneyValue {
  /** Plain decimal string. Never pass a float, valuation, or formatted number. */
  amount: string;
  currency: string;
  /**
   * Digits after the point; default 2 (money). A token leg carries its mint's
   * decimals so a settled receipt keeps every digit the chain recorded.
   */
  precision?: number;
}

export interface OperationReceipt {
  id: string;
  operationId: string;
  status: "settled";
  amount: MoneyValue;
  accountLabel: string;
  counterparty: string;
  fee?: MoneyValue;
  total?: MoneyValue;
  timestampLabel?: string;
}

interface OperationState { operationId: string }
export type ExecutionState =
  | (OperationState & { status: "draft" })
  | (OperationState & { status: "approval-required" })
  | (OperationState & { status: "submitting" })
  | (OperationState & { status: "settled"; expectedReceiptId: string; receipt: OperationReceipt })
  | (OperationState & { status: "failed"; reason: string; retrySafe: boolean })
  | (OperationState & { status: "unknown"; reason: string });

export type DisplayStatus = ExecutionState["status"] | "paused" | "partial" | "active" | "waiting-for-input";

/** Defensive ownership check. Adapters must still verify chain finality and amounts. */
export function hasOwnReceipt(state: ExecutionState): state is Extract<ExecutionState, { status: "settled" }> {
  return state.status === "settled"
    && nonempty(state.operationId)
    && nonempty(state.expectedReceiptId)
    && state.receipt?.status === "settled"
    && state.receipt.id === state.expectedReceiptId
    && state.receipt.operationId === state.operationId
    && nonempty(state.receipt.accountLabel)
    && nonempty(state.receipt.counterparty)
    && validMoney(state.receipt.amount)
    && (state.receipt.fee === undefined || validMoney(state.receipt.fee))
    && (state.receipt.total === undefined || validMoney(state.receipt.total));
}

function nonempty(value: unknown): value is string {
  return typeof value === "string" && value.trim().length > 0;
}

function validMoney(value: MoneyValue | undefined): boolean {
  return Boolean(value && typeof value.amount === "string" && typeof value.currency === "string" && value.currency.trim() && formatMoney(value.amount, "amount", value.precision) !== null);
}

export function executionStatus(state: ExecutionState): ExecutionState["status"] {
  return state.status === "settled" && !hasOwnReceipt(state) ? "unknown" : state.status;
}
